import { useAppDispatch } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  setSearchLoading,
  setSearchParamsForCategory,
  PageSearchParams,
  resetSearchParamsForCategory,
} from '@backrs_ui/grow-ui/src/redux/search/search';

import { SearchCategory, SearchPageCategory } from '../search.types';
import { handleInitiateSearch } from '../search.util';

type UseSearchActionsProps = Readonly<{
  currentPageCategory: SearchPageCategory;
  searchParams: PageSearchParams;
  currentSearchTerm: string;
  selectedFilters: SearchCategory[];
  hasInitialSearchBeenMade?: boolean;
}>;

/**
 * Custom hook for managing search-related actions and state updates
 */
export const useSearchActions = ({
  currentPageCategory,
  searchParams,
  currentSearchTerm,
  selectedFilters,
  hasInitialSearchBeenMade,
}: UseSearchActionsProps) => {
  const dispatch = useAppDispatch();

  /**
   * Initiates a search with current parameters
   */
  const initiateSearch = (items?: SearchCategory[]) => {
    handleInitiateSearch({
      dispatch,
      currentSearchTerm,
      pageNum: 1,
      items: items || selectedFilters,
      searchPageCategory: currentPageCategory,
      hasInitialSearchBeenMade,
    });
  };

  /**
   * Handles updating selected filter items and initiating search if needed
   */
  const handleSettingSelectedItems = (items: SearchCategory[]) => {
    if (items.length === 0 && currentSearchTerm === '') {
      dispatch(resetSearchParamsForCategory(currentPageCategory));

      return;
    }

    // If there are no items selected and no search term, don't initiate search
    dispatch(setSearchLoading(true));
    dispatch(
      setSearchParamsForCategory({
        category: currentPageCategory,
        searchParams: {
          ...searchParams,
          selectedFilters: items,
        },
      }),
    );

    if ((!currentSearchTerm || currentSearchTerm === '') && items.length === 0) {
      return;
    }

    initiateSearch(items);
  };

  /**
   * Handles updating the search term in the Redux store
   */
  const handleUpdateSearchTerm = (searchTerm: string) => {
    dispatch(
      setSearchParamsForCategory({
        category: currentPageCategory,
        searchParams: {
          ...searchParams,
          searchTerm,
        },
      }),
    );
  };

  return {
    handleSettingSelectedItems,
    handleUpdateSearchTerm,
    initiateSearch,
  };
};
